import React, {useContext} from 'react';
import _ from "lodash";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {ApiUtility} from "../common/ApiUtility";
import {Constants} from "../common/Constants";
import {ActivityMaterial} from "../../types/ActivityMaterial";
import {ActivityScope} from "../../types/ActivityScope";
import {CheckBox} from "../common/CheckBox";
import {CheckBoxSet} from "../common/CheckBoxSet";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PillButton} from "../common/PillButton";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {GlobalContext} from "../admin-frame/AdminFrame";

interface ActivityMaterialFormProps {
    activityMaterial?: ActivityMaterial;
    save: Function;
    isNew?: boolean;
}

interface ActivityMaterialFormState {
    name: string;
    measurement_unit: string;
    active: boolean;
    activity_scope_ids: number[];
    activityScopesList: ActivityScope[];
    loading: boolean;
}

export let ActivityMaterialForm = function (props: ActivityMaterialFormProps) {
    const context = useContext(GlobalContext);
    const activityMaterial = _.clone(props.activityMaterial) || undefined;
    const materialsListPath = `/${Constants.routes.settings.pathSegment}/activity-materials`;

    const initialModelState: ActivityMaterialFormState = {
        name: activityMaterial?.name || '',
        measurement_unit: activityMaterial?.measurement_unit || '',
        active: activityMaterial?.active ?? true,
        activity_scope_ids: (activityMaterial?.activity_scopes || []).map(function (activityScope) {
            return activityScope.id;
        }),
        activityScopesList: [],
        loading: false,
    };

    const formEngine = useModelFormEngine(
        initialModelState, activityMaterial, fetchSupportingData, validateForm, formStateToSaveData, props.save
    );

    function fetchSupportingData() {
        return ApiUtility.getActivityScopes().then(function (activityScopesList: ActivityScope[]) {
            return {activityScopesList: activityScopesList};
        });
    }

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve) {
            let validationErrors = [];
            let invalidFields = [];

            if (!formEngine.dataStore.data().name) {
                validationErrors.push('Please enter a material name.');
                invalidFields.push('name');
            }

            return handleValidationResult(validationErrors, invalidFields, resolve);
        });
    }

    function formStateToSaveData(formState: ActivityMaterialFormState) {
        let saveData: {
            id?: number;
            name: string;
            measurement_unit: string;
            active: boolean;
            activity_scope_ids: number[]
        } = {
            name: formState.name,
            measurement_unit: formState.measurement_unit,
            active: formState.active,
            activity_scope_ids: formState.activity_scope_ids,
        };
        if (activityMaterial) {
            saveData.id = activityMaterial.id;
        }
        return saveData;
    }

    const scopeOptions = formEngine.dataStore.data().activityScopesList.map(function (activityScope: ActivityScope) {
        const inactiveSuffix = activityScope.active ? '' : ' (Inactive)';
        return {value: activityScope.id, label: `${activityScope.name}${inactiveSuffix}`, inactive: !activityScope.active};
    });
    const checkedScopeIds = formEngine.dataStore.data().activity_scope_ids;
    const allScopesChecked = scopeOptions.length > 0 && scopeOptions.every(function (scopeOption) {
        return checkedScopeIds.includes(scopeOption.value);
    });

    function onChangeCheckedScopes(updatedCheckedScopeIds: number[]) {
        formEngine.updateAndRenderUnsavedData({activity_scope_ids: updatedCheckedScopeIds});
    }

    function onChangeSelectAllScopes(checked: boolean) {
        formEngine.updateAndRenderUnsavedData({
            activity_scope_ids: checked ? scopeOptions.map((scopeOption) => scopeOption.value) : [],
        });
    }

    return (
        <div className="common-form-container">
            <ConditionalRenderer
                if={formEngine.dataStore.data().loading}
                renderer={() => (<OverlayLoadingIndicator/>)}
            />
            <div className="common-form narrow-form settings-form activity-material-form">
                <div className="settings-form-back-link"
                     onClick={() => context.navigator.navigateTo(materialsListPath)}>
                    <img className="settings-form-back-link-chevron" src="/static/images/chevron-left-textcolor.svg"/>
                    BACK TO MATERIALS
                </div>
                <div className="settings-form-title">{props.isNew ? "Add Material" : "Edit Material"}</div>
                <div className="common-form-body">
                    <div className="common-form-body-row">
                        <div className="common-form-body-row-horizontal">
                            <div className="common-form-body-row-horizontal-cell">
                                <div className={formEngine.getFormFieldClasses("common-form-field", "name")}>
                                    <div className="common-form-field-label">Material Name</div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="name"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().name}
                                            onChange={(event) => formEngine.handleTextFieldChange("name", event)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="common-form-body-row-horizontal-cell">
                                <div
                                    className={formEngine.getFormFieldClasses("common-form-field", "measurement_unit")}>
                                    <div className="common-form-field-label">Unit of Measure</div>
                                    <div className="common-form-field-input-container">
                                        <input
                                            id="measurement_unit"
                                            type="text"
                                            className="common-form-input"
                                            value={formEngine.dataStore.data().measurement_unit}
                                            onChange={(event) => formEngine.handleTextFieldChange("measurement_unit", event)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="settings-form-divider"></div>
                    <div className="common-form-body-row settings-form-checkbox-section">
                        <div className="settings-form-checkbox-section-header">
                            <div className="common-form-field-label">SCOPES</div>
                            <div className="common-form-checkbox-set-select-all">
                                <CheckBox
                                    id="activityMaterialScopesSelectAll"
                                    checked={allScopesChecked}
                                    onChange={onChangeSelectAllScopes}
                                    label="SELECT ALL"
                                />
                            </div>
                        </div>
                        <div className="settings-form-checkbox-section-help-text">
                            This material will display for the below checked scopes.
                        </div>
                        <CheckBoxSet
                            options={scopeOptions}
                            values={checkedScopeIds}
                            onChange={onChangeCheckedScopes}
                        />
                    </div>
                    <div className="settings-form-divider"></div>
                    <div className="common-form-body-row active-checkbox-row">
                        <CheckBox
                            id="activity-material-form-active-checkbox"
                            checked={formEngine.dataStore.data().active}
                            onChange={(checked: boolean) => formEngine.handleCheckboxChange("active", checked)}
                            label="ACTIVE"
                        />
                    </div>
                    <div className="common-form-body-row">
                        <div className="common-form-action-buttons-row">
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="solid-green-button submit-button"
                                onClick={formEngine.onClickSave}
                                buttonText={props.isNew ? "CREATE" : "SAVE"}
                            />
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="green-border-button cancel-button"
                                onClick={() => context.navigator.navigateTo(materialsListPath)}
                                buttonText="CANCEL"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker}/>
        </div>
    );
};
